/**
 * Everything this app keeps in localStorage, under keys that say who owns them and which shape they
 * are in: `andromeda-ui.<what>.v<n>`. The namespace matters because the console is served from the
 * same origin as whatever else is deployed behind that host, and the version is what lets a stored
 * shape be retired rather than mis-read.
 *
 * Reads return plain objects to be narrowed by their owner: what comes back from storage is a
 * tester's old settings, a hand-edited value, or another build's shape, none of which can be taken
 * on trust. Every call is guarded — storage denied in a private window is not a reason to stop
 * working.
 */

const APP_NAMESPACE = 'andromeda-ui';

/**
 * Builds a namespaced, versioned storage key.
 *
 * @param name what is stored, e.g. `settings`
 * @param version the shape's version, bumped when old values can no longer be read
 * @returns the key to store it under
 */
export function storageKey(name: string, version = 1): string {
  return `${APP_NAMESPACE}.${name}.v${version}`;
}

/**
 * Reads a stored JSON object.
 *
 * @param key the storage key
 * @returns its fields, unnarrowed, or null when absent, unreadable or not an object
 */
export function readStoredObject(key: string): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return parsed !== null && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Stores a value as JSON.
 *
 * @param key the storage key
 * @param value the value to store
 */
export function writeStored(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage denied or full. The setting still applies to this session.
  }
}

/**
 * Moves a value written under an older key to its current one, once. A tester's endpoint and
 * receiver identity are worth keeping across a rename: losing them silently looks like the console
 * forgetting which backend it was pointed at.
 *
 * @param legacyKey the key a previous build wrote
 * @param key the key this build reads
 */
export function migrateLegacyKey(legacyKey: string, key: string): void {
  try {
    const legacy = localStorage.getItem(legacyKey);
    if (legacy === null) return;
    if (localStorage.getItem(key) === null) localStorage.setItem(key, legacy);
    localStorage.removeItem(legacyKey);
  } catch {
    // Nothing to migrate that is worth failing over.
  }
}

/** Narrows a stored field to a non-empty trimmed string, or null. */
export function storedText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}
